/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import redux_flow_with_middlewareImg from 'assets/images/redux-flow-with-middleware.png'
import middleware_stackImg from 'assets/images/middleware-stack.png'

export const _1 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Redux middleware</h2>
    <p className={styles.description}>
      <b>Middleware (прослойка)</b> - общая концепция в программном обеспечении, инструмент для расширения функционала,
      находящийся между двух точек в логической цепочке исполнения кода.{' '}
    </p>
    <p className={styles.description}>
      Прослойка это функция промежуточной обработки, которая получает некие данные, может их обработать и, если
      необходимо, передать дальше по цепочке. Можно использовать множество прослоек в одном проекте.{' '}
    </p>
    <p className={styles.description}>
      Redux-прослойки предоставляют точку расширения между отправкой действия и моментом, когда это действие достигает
      редюсера. Прослойки используются для логирования, сообщения об ошибках, обработки HTTP-запросов, аналитики и т.д.{' '}
    </p>
    <img className={styles.img} src={redux_flow_with_middlewareImg} alt="redux-flow-with-middleware" />
    <ul className={styles.list}>
      <li className={styles.list__item}>
        Перед тем как дейсвия попадают в редюсеры, они проходят через цепочку (стек) прослоек.{' '}
      </li>
      <li className={styles.list__item}>Прослойки получают доступ ко всем действиям. </li>
      <li className={styles.list__item}>
        Если прослойка рассчитана на работу с каким-то действием, она может его изменять, отменять или выполнять
        какие-то другие дейтсвия.{' '}
      </li>
      <li className={styles.list__item}>
        Если прослойка не рассчитана на работу с определенным типом действий, она просто пропускает действие через себя
        без изменений.{' '}
      </li>
      <li className={styles.list__item}>
        После того как действие прошло всю цепочку прослоек, оно попадает в редюсеры.
      </li>
    </ul>
    <img className={styles.img} src={middleware_stackImg} alt="middleware-stack" />
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.1 - Cоздание прослоек</h3>
      <p className={styles.description}>
        Redux написан с применением функционального программирования, поэтому для создания прослоек используется{' '}
        <a
          className={styles.link}
          href="http://prgssr.ru/development/vvedenie-v-karrirovanie-v-javascript.html"
          target="_blank"
        >
          каррированая функция
        </a>
        .
      </p>
      <CopyBlock
        text={`const middleware = store => next => action => {};`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>store</code> - ссылка на Redux-хранилище
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>action</code> - текущее действие
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>next</code> - функция которую необходимо вызвать когда прослойка закончила
          обработку действия, для того чтобы отправить его дальше по цепочке прослоек или в редюсеры.
        </li>
      </ul>
      <p className={styles.description}>
        Для того чтобы использовать прослойку необходимо добавить ее при создании хранилища. Для этого у Redux есть
        функция <code className={styles.code}>applyMiddleware</code>, которая принимает произвольное количество
        аргументов. Результат ее вызова мы передаем как аргумент <code className={styles.code}>enhancer</code> в{' '}
        <code className={styles.code}>createStore()</code>.{' '}
      </p>
      <CopyBlock
        text={`import { createStore, applyMiddleware } from 'redux';

const reducer = state => state;

const loggerMiddleware = store => next => action => {
  console.group(action.type);
  console.info('dispatching', action);
  console.groupEnd(action.type);

  return next(action);
};

const enhancer = applyMiddleware(loggerMiddleware);

const store = createStore(reducer, enhancer);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
  </section>
))
